<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import Stepper from "../../components/Stepper.svelte";
  import TalkTrackNotes from "./TalkTrackNotes.svelte";
  import NextStepsPanel from "./NextStepsPanel.svelte";
  import { selectExecutiveHighlights } from "$core/report/executiveHighlights";
  import { formatScore } from "$core/format/numberFormat";
  import type { DiagnosticResult } from "$core/diagnostic.types";
  import type { Recommendation } from "$core/recommendations/recommendation.types";
  import type { ReportSectionId } from "$core/report/report.types";

  export let result: DiagnosticResult;

  type PresentationStepId = Extract<
    ReportSectionId,
    "data-base" | "executive-summary" | "score" | "recommendations" | "next-steps"
  >;

  /** IDs das secoes exibidas no stepper de apresentacao (5 etapas). */
  const PRESENTATION_STEP_IDS: PresentationStepId[] = [
    "data-base",
    "executive-summary",
    "score",
    "recommendations",
    "next-steps",
  ];

  const STEPPER_LABELS: Record<PresentationStepId, string> = {
    "executive-summary": "5 Numeros",
    "data-base": "Base",
    score: "Score",
    recommendations: "Recomendacoes",
    "next-steps": "Proximos Passos",
  };

  const steps = PRESENTATION_STEP_IDS.map((id) => ({
    id,
    label: STEPPER_LABELS[id] ?? id,
  }));

  let currentIndex = 0;

  $: currentStepId = PRESENTATION_STEP_IDS[currentIndex] as PresentationStepId;

  $: isFirst = currentIndex === 0;
  $: isLast = currentIndex === PRESENTATION_STEP_IDS.length - 1;

  $: highlights = selectExecutiveHighlights(result);
  $: recommendations = result.recommendations as Recommendation[];

  function goTo(index: number): void {
    if (index >= 0 && index < PRESENTATION_STEP_IDS.length) {
      currentIndex = index;
    }
  }

  function goNext(): void {
    goTo(currentIndex + 1);
  }

  function goPrev(): void {
    goTo(currentIndex - 1);
  }

  /** Emitido quando o usuario pressiona Esc para voltar ao Dashboard. */
  function handleExit(): void {
    dispatch("exit");
  }

  import { createEventDispatcher } from "svelte";
  const dispatch = createEventDispatcher<{ exit: void }>();

  function handleKeydown(event: KeyboardEvent): void {
    switch (event.key) {
      case "ArrowRight":
      case " ":
        event.preventDefault();
        goNext();
        break;
      case "ArrowLeft":
        event.preventDefault();
        goPrev();
        break;
      case "Escape":
        event.preventDefault();
        handleExit();
        break;
    }
  }

  onMount(() => {
    window.addEventListener("keydown", handleKeydown);
  });

  onDestroy(() => {
    window.removeEventListener("keydown", handleKeydown);
  });
</script>

<div class="presentation" data-testid="presentation-mode">
  <!-- Stepper de navegacao no topo -->
  <header class="presentation__header">
    <div class="presentation__stepper-wrap">
      <Stepper {steps} currentStep={currentStepId} />
    </div>
    <button
      class="presentation__exit-btn"
      type="button"
      aria-label="Sair do modo apresentacao"
      on:click={handleExit}
    >
      ✕ Sair
    </button>
  </header>

  <!-- Conteudo principal + notas laterais -->
  <div class="presentation__body">
    <main class="presentation__content" aria-live="polite" aria-atomic="true">
      {#if currentStepId === "data-base"}
        <h2 class="presentation__section-title">Base Analisada</h2>
        <p class="presentation__section-intro">Visibilidade geral dos dados do período.</p>
        <ul class="presentation__highlights">
          <li class="presentation__highlight">
            <span class="presentation__highlight-value">{result.metrics.visibility.analyzedEmployees.value}</span>
            <span class="presentation__highlight-label">Colaboradores</span>
          </li>
          <li class="presentation__highlight">
            <span class="presentation__highlight-value">{result.metrics.visibility.analyzedDays.value}</span>
            <span class="presentation__highlight-label">Dias analisados</span>
          </li>
          <li class="presentation__highlight">
            <span class="presentation__highlight-value">{result.metrics.visibility.totalPunches.value}</span>
            <span class="presentation__highlight-label">Marcações</span>
          </li>
        </ul>
      {:else if currentStepId === "executive-summary"}
        <h2 class="presentation__section-title">5 Números</h2>
        <p class="presentation__section-intro">Principais indicadores do diagnóstico.</p>
        <ul class="presentation__highlights">
          {#each highlights as hl (hl.code)}
            <li class="presentation__highlight">
              <span class="presentation__highlight-value">{hl.formattedValue}</span>
              <span class="presentation__highlight-label">{hl.label}</span>
              <span class="presentation__highlight-explanation">{hl.explanation}</span>
            </li>
          {/each}
        </ul>
      {:else if currentStepId === "score"}
        <h2 class="presentation__section-title">Score de Maturidade</h2>
        <div class="presentation__score">
          <span class="presentation__score-total"
            >{formatScore(result.score.total)}<span class="presentation__score-max">/100</span></span
          >
          <span class="presentation__score-band">{result.score.band}</span>
        </div>
        <p class="presentation__section-intro">{result.score.pilotNotice}</p>
      {:else if currentStepId === "recommendations"}
        <h2 class="presentation__section-title">Recomendações</h2>
        <ul class="presentation__rec-list">
          {#each recommendations as rec (rec.code)}
            <li class="presentation__rec-item">
              <strong>{rec.finding}</strong>
              <span>{rec.message}</span>
            </li>
          {/each}
        </ul>
      {:else if currentStepId === "next-steps"}
        <h2 class="presentation__section-title">Próximos Passos</h2>
        <NextStepsPanel {recommendations} />
      {:else}
        <p class="presentation__empty">Seção não disponível no relatório.</p>
      {/if}
    </main>

    <!-- Painel lateral com notas do consultor (interno — nao vai para export) -->
    <aside class="presentation__sidebar">
      <TalkTrackNotes section={currentStepId} />
    </aside>
  </div>

  <!-- Navegacao inferior -->
  <footer class="presentation__footer">
    <button
      class="presentation__nav-btn presentation__nav-btn--prev"
      type="button"
      disabled={isFirst}
      aria-label="Secao anterior"
      on:click={goPrev}
    >
      ← Anterior
    </button>

    <span class="presentation__progress" aria-live="polite">
      {currentIndex + 1} / {PRESENTATION_STEP_IDS.length}
    </span>

    <button
      class="presentation__nav-btn presentation__nav-btn--next"
      type="button"
      disabled={isLast}
      aria-label="Proxima secao"
      on:click={goNext}
    >
      Proxima →
    </button>
  </footer>
</div>

<style>
  .presentation {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: var(--ptt-white);
  }

  /* Header */
  .presentation__header {
    display: flex;
    align-items: center;
    gap: var(--ptt-space-4);
    padding: var(--ptt-space-4) var(--ptt-space-6);
    border-bottom: 1px solid var(--ptt-gray-20);
    background: var(--ptt-white);
    position: sticky;
    top: 0;
    z-index: 10;
  }

  .presentation__stepper-wrap {
    flex: 1;
    min-width: 0;
  }

  .presentation__exit-btn {
    background: transparent;
    border: 1px solid var(--ptt-gray-20);
    border-radius: var(--ptt-radius-sm);
    color: var(--ptt-gray-200, #555);
    font-size: 0.875rem;
    font-weight: 600;
    padding: var(--ptt-space-2) var(--ptt-space-4);
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease;
  }

  .presentation__exit-btn:hover {
    background: var(--ptt-gray-10);
  }

  /* Body layout */
  .presentation__body {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: var(--ptt-space-6);
    flex: 1;
    padding: var(--ptt-space-8) var(--ptt-space-6);
    align-items: start;
  }

  @media (max-width: 900px) {
    .presentation__body {
      grid-template-columns: 1fr;
    }
  }

  /* Main content */
  .presentation__content {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-5);
  }

  .presentation__section-title {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: var(--ptt-gray-300);
    line-height: 1.2;
  }

  .presentation__section-intro {
    margin: 0;
    font-size: 1.125rem;
    line-height: 1.6;
    color: var(--ptt-gray-200, #555);
  }

  .presentation__highlights {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--ptt-space-4);
  }

  .presentation__highlight {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
    background: var(--ptt-gray-10);
    border-radius: var(--ptt-radius-md);
    padding: var(--ptt-space-4);
    min-width: 180px;
  }

  .presentation__highlight-value {
    font-size: 1.75rem;
    font-weight: 700;
    color: var(--ptt-purple-100);
  }

  .presentation__highlight-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--ptt-gray-300);
  }

  .presentation__highlight-explanation {
    font-size: 0.8125rem;
    color: var(--ptt-gray-200, #555);
    line-height: 1.4;
  }

  .presentation__score {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-2);
  }

  .presentation__score-total {
    font-size: 4rem;
    font-weight: 700;
    color: var(--ptt-purple-100);
    line-height: 1;
  }

  .presentation__score-max {
    font-size: 1.5rem;
    color: var(--ptt-gray-100);
    font-weight: 400;
  }

  .presentation__score-band {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--ptt-gray-300);
    text-transform: capitalize;
  }

  .presentation__rec-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-4);
  }

  .presentation__rec-item {
    display: flex;
    flex-direction: column;
    gap: var(--ptt-space-1);
    background: var(--ptt-gray-10);
    border-radius: var(--ptt-radius-md);
    padding: var(--ptt-space-4);
    font-size: 0.9375rem;
    color: var(--ptt-gray-300);
  }

  .presentation__empty {
    color: var(--ptt-gray-100);
    font-size: 0.9375rem;
    margin: 0;
  }

  /* Sidebar */
  .presentation__sidebar {
    position: sticky;
    top: calc(var(--ptt-space-8) + 64px);
  }

  /* Footer navigation */
  .presentation__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--ptt-space-4) var(--ptt-space-6);
    border-top: 1px solid var(--ptt-gray-20);
    background: var(--ptt-white);
    position: sticky;
    bottom: 0;
  }

  .presentation__nav-btn {
    display: inline-flex;
    align-items: center;
    gap: var(--ptt-space-2);
    padding: var(--ptt-space-2) var(--ptt-space-5);
    font-family: var(--ptt-font-sans);
    font-size: 1rem;
    font-weight: 600;
    border-radius: var(--ptt-radius-sm);
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease;
    white-space: nowrap;
  }

  .presentation__nav-btn--prev {
    background: transparent;
    color: var(--ptt-purple-100);
    border: 2px solid var(--ptt-purple-100);
  }

  .presentation__nav-btn--prev:hover:not(:disabled) {
    background: var(--ptt-gray-10);
  }

  .presentation__nav-btn--next {
    background: var(--ptt-yellow-100);
    color: var(--ptt-black);
    border: none;
  }

  .presentation__nav-btn--next:hover:not(:disabled) {
    background: var(--ptt-yellow-200);
  }

  .presentation__nav-btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }

  .presentation__progress {
    font-size: 0.875rem;
    color: var(--ptt-gray-200, #555);
    font-weight: 500;
  }
</style>
